import apiClient from './apiClient';

/**
 * Full profile JSON including documents metadata.
 */
export async function fetchProfile() {
  const { data } = await apiClient.get('/profile');
  return data;
}

/**
 * Update profile fields. Leave passportNumber/ssn blank to keep existing encrypted values on the server.
 * @param {object} payload
 */
export async function updateProfile(payload) {
  const { data } = await apiClient.put('/profile', payload);
  return data;
}

/**
 * @param {File} file
 * @param {'CONTRACT'|'RESUME'|'PASSPORT'|'DRIVER_LICENSE'|'SSN_CARD'|'OTHER'} docType
 */
export async function uploadProfileDocument(file, docType) {
  const body = new FormData();
  body.append('file', file);
  body.append('docType', docType);
  const { data } = await apiClient.post('/profile/documents', body);
  return data;
}

export async function deleteProfileDocument(documentId) {
  await apiClient.delete(`/profile/documents/${documentId}`);
}

function filenameFromContentDisposition(disposition) {
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition || '');
  const asciiMatch = /filename="([^"]+)"/i.exec(disposition || '');
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  if (asciiMatch) return asciiMatch[1];
  return null;
}

/**
 * Latest profile RESUME as a File (for flows that post multipart, e.g. resume evaluation).
 * @returns {Promise<File|null>} null if none; throws on network error
 */
export async function fetchProfileResumeAsFile() {
  const profile = await fetchProfile();
  const resumeDocs = Array.isArray(profile?.documents)
    ? profile.documents.filter((d) => d?.docType === 'RESUME')
    : [];
  if (resumeDocs.length === 0) return null;
  const latest = [...resumeDocs].sort(
    (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
  )[0];
  const id = latest.id;
  if (id == null) return null;

  const res = await apiClient.get(`/profile/documents/${id}/download`, { responseType: 'blob' });
  const disposition = res.headers?.['content-disposition'] || '';
  let filename = filenameFromContentDisposition(disposition) || latest.originalFilename || 'resume.pdf';
  const mimeType =
    res.headers?.['content-type']?.split(';')[0]?.trim() ||
    (/\.docx$/i.test(filename)
      ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      : 'application/pdf');
  const blob =
    res.data instanceof Blob ? res.data : new Blob([res.data], { type: mimeType || 'application/octet-stream' });
  return new File([blob], filename, {
    type: mimeType || 'application/octet-stream',
  });
}

/** Download user's own uploaded file via authenticated request. */
export async function downloadProfileDocument(documentId) {
  const res = await apiClient.get(`/profile/documents/${documentId}/download`, { responseType: 'blob' });
  const disposition = res.headers?.['content-disposition'] || '';
  let filename = filenameFromContentDisposition(disposition) || 'document';

  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
